/**
 * Font files for production texts.
 *
 * Names and numbers are drawn as outlines from the font file, with opentype.js.
 * Customers can upload their own fonts as ttf, otf, woff or woff2
 * (CustomFont::ALLOWED_EXTENSIONS); opentype.js reads the first three but has no
 * Brotli decoder, so a WOFF2 upload was unreadable and its texts were dropped from
 * the file. WOFF2 is unpacked to plain TrueType/OpenType first — the decoder is
 * loaded only when a WOFF2 file actually turns up.
 */

/** 'wOF2' — the first four bytes of every WOFF2 file. */
const WOFF2_SIGNATURE = 0x774f4632;

export function isWoff2(bytes: ArrayBuffer): boolean {
  return bytes.byteLength >= 4 && new DataView(bytes).getUint32(0) === WOFF2_SIGNATURE;
}

/** Plain sfnt (TrueType/OpenType) bytes that opentype.js can parse. */
export async function toParsableFontBytes(bytes: ArrayBuffer): Promise<ArrayBuffer> {
  if (!isWoff2(bytes)) return bytes;
  const { default: decompress } = await import('woff2-encoder/decompress');
  const sfnt = await decompress(bytes);
  return sfnt.buffer.slice(sfnt.byteOffset, sfnt.byteOffset + sfnt.byteLength) as ArrayBuffer;
}

/**
 * Fetch and parse a font file. Resolves the parsed font, or a reason it could
 * not be used — the engine reports it instead of silently dropping the text.
 */
export async function loadFontFile<T>(
  url: string,
  parse: (bytes: ArrayBuffer) => T
): Promise<{ font: T } | { error: 'not-found' | 'unreadable' }> {
  let bytes: ArrayBuffer;
  try {
    const response = await fetch(url);
    if (!response.ok) return { error: 'not-found' };
    bytes = await response.arrayBuffer();
  } catch {
    return { error: 'not-found' };
  }
  try {
    return { font: parse(await toParsableFontBytes(bytes)) };
  } catch {
    return { error: 'unreadable' };
  }
}
